import { useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { btn } from "./kit";

function Page({ vid, n }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/policies/versions/${vid}/pages/${n}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [vid, n]);
  return url ? <img src={url} alt={`Page ${n + 1}`} className="mx-auto mb-4 block w-full max-w-3xl rounded border bg-white shadow-sm" data-testid={`reader-page-${n}`} />
    : <div className="mx-auto mb-4 aspect-[8.5/11] w-full max-w-3xl animate-pulse rounded border bg-muted" />;
}

export default function PolicyReader({ x, onClose, onRead }) {
  const vid = x.version.id;
  const [count, setCount] = useState(null);
  const [done, setDone] = useState(x.read);
  const [pct, setPct] = useState(0);
  const box = useRef(null);
  const end = useRef(null);
  useEffect(() => { api.get(`/policies/versions/${vid}/pages`).then((r) => setCount(r.data.count)).catch((e) => toast.error(errMsg(e))); }, [vid]);
  useEffect(() => {
    if (!count || done || !end.current) return;
    const ob = new IntersectionObserver(async ([e]) => {
      if (!e.isIntersecting || box.current.scrollTop < 40 && box.current.scrollHeight > box.current.clientHeight + 40) return;
      ob.disconnect();
      try { await api.post(`/policies/versions/${vid}/read`); setDone(true); onRead(); } catch (err) { toast.error(errMsg(err)); }
    }, { root: box.current, threshold: 1 });
    ob.observe(end.current);
    return () => ob.disconnect();
  }, [count, done, vid, onRead]);
  const onScroll = (e) => { const t = e.currentTarget; setPct(Math.min(100, Math.round((t.scrollTop / Math.max(1, t.scrollHeight - t.clientHeight)) * 100))); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[92vh] max-w-4xl flex-col gap-3 p-4 sm:p-6" data-testid="policy-reader">
        <DialogHeader><DialogTitle>{x.title} · v{x.version.version}</DialogTitle>
          <DialogDescription>{done ? "You've read to the end. Close this to sign." : "Scroll through to the end. The sign-off unlocks once you reach the last page."}</DialogDescription></DialogHeader>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full bg-[color:var(--dcbc-red)] transition-[width]" style={{ width: `${done ? 100 : pct}%` }} data-testid="reader-progress" /></div>
        <div ref={box} onScroll={onScroll} className="flex-1 overflow-y-auto rounded-lg bg-muted/40 p-3" data-testid="reader-scroll">
          {count === null ? <div className="mx-auto aspect-[8.5/11] w-full max-w-3xl animate-pulse rounded bg-muted" /> : Array.from({ length: count }, (_, n) => <Page key={n} vid={vid} n={n} />)}
          <div ref={end} className="py-6 text-center text-sm font-semibold text-muted-foreground" data-testid="reader-end">{done ? <span className="inline-flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" /> End of policy</span> : "End of policy"}</div>
        </div>
        <button className={btn.primary} disabled={!done} onClick={onClose} data-testid="reader-done">{done ? "Done — go to sign" : "Keep scrolling to the end"}</button>
      </DialogContent>
    </Dialog>
  );
}
